import { Box, Unstable_Grid2 as Grid, Stack, Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Seo } from "src/components/seo";
import { usePageView } from "src/hooks/use-page-view";
import { tokens } from "src/locales/tokens";
import { useDispatch } from "src/store";

import type { Page as PageType } from "src/types/page";
import { thunks } from "src/thunks/analyze";
import { InstagramGeneratedData, InstagramProfile } from "src/types/analysis";
import { CardReviewTemplate } from "src/sections/instagram/card-review-template";
import { SimilarAccounts } from "src/sections/instagram/similar-accounts";
import { InstagramSearchForm } from "src/sections/instagram/instagram-search-form";
import { decodeUnicodeEscapeSequences } from "src/utils/string";
import { useParams } from "react-router";
import { InstagramProfileCard } from "src/sections/instagram/instagram-profile-card";
import { InstagramProfileEngagementRateCard } from "src/sections/instagram/instagram-profile-engagement-rate-card";
import { useRouter } from "src/hooks/use-router";
import {
  getEngagementRateMessages,
  getGeneralContentReviewMessages,
  getGeneralOverviewMessages,
  getStrengthMessages,
  getMonetizationMessages,
  getWeaknessMessages,
} from "src/sections/instagram/insta-prompt-helpers";
import { CreateContentStrategy } from "src/sections/instagram/create-content-strategy";

const Page: PageType = () => {
  usePageView();

  const { username } = useParams();
  const { t } = useTranslation();

  const [instagramAccount, setInstagramAccount] =
    React.useState<InstagramProfile | null>(null);
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = React.useState(false);
  const [hasGeneralError, setHasGeneralError] = React.useState(false);
  const [hasUserNotFoundError, setHasUserNotFoundError] = React.useState(false);
  const router = useRouter();

  function formatGeneratedData(data: InstagramProfile | null) {
    if (data) {
      data.generatedData = {
        overview: decodeUnicodeEscapeSequences(
          data.generatedData.overview ?? ""
        ).replace(/\\n/g, "\n"),
        content: decodeUnicodeEscapeSequences(
          data.generatedData.content ?? ""
        ).replace(/\\n/g, "\n"),
        engagement: decodeUnicodeEscapeSequences(
          data.generatedData.engagement ?? ""
        ).replace(/\\n/g, "\n"),
        strengths: decodeUnicodeEscapeSequences(
          data.generatedData.strengths ?? ""
        ).replace(/\\n/g, "\n"),
        weaknesses: decodeUnicodeEscapeSequences(
          data.generatedData.weaknesses ?? ""
        ).replace(/\\n/g, "\n"),
        recommendations: decodeUnicodeEscapeSequences(
          data.generatedData.recommendations ?? ""
        ).replace(/\\n/g, "\n"),
      };
    }
  }

  const onSubmit = React.useCallback(
    async (username: string): Promise<void> => {
      router.push("/instagram/" + username);
      setIsLoading(true);
      setHasUserNotFoundError(false);
      setHasGeneralError(false);
      setInstagramAccount(null);
      try {
        var data = (await dispatch(
          thunks.analyzeInstagram(username)
        )) as InstagramProfile | null;
        formatGeneratedData(data);
        setInstagramAccount(data);
      } catch (error) {
        if (error?.code === "Instagram.InstagramNotFound") {
          setInstagramAccount(null);
          setHasUserNotFoundError(true);
        } else {
          setHasGeneralError(true);
        }
      }

      setIsLoading(false);
    },
    [dispatch, router]
  );

  const loadInstagramProfile = useCallback(
    async (username: string): Promise<void> => {
      setIsLoading(true);
      setHasUserNotFoundError(false);
      setHasGeneralError(false);
      try {
        const data = (await dispatch(
          thunks.getInstagramProfile(username)
        )) as InstagramProfile | null;

        formatGeneratedData(data);
        setInstagramAccount(data);
      } catch (error) {
        if (error?.code === "Instagram.InstagramNotFound") {
          setInstagramAccount(null);
          await onSubmit(username);
          //setHasUserNotFoundError(true);
        } else {
          setHasGeneralError(true);
        }
      }
      setIsLoading(false);
    },
    [dispatch, onSubmit]
  );

  const updateInstagramProfile = useCallback(
    async (data: InstagramGeneratedData): Promise<void> => {
      await dispatch(thunks.updateInstagramProfile(instagramAccount!.id, data));
    },
    [dispatch, instagramAccount]
  );

  useEffect(() => {
    const fetchData = async () => {
      if (username) {
        await loadInstagramProfile(username);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const engagementRateMessages = useMemo(() => {
    if (!instagramAccount) {
      return [];
    }

    return getEngagementRateMessages(instagramAccount.data);
  }, [instagramAccount]);

  const generalContentReviewMessages = useMemo(() => {
    if (!instagramAccount) {
      return [];
    }
    return getGeneralContentReviewMessages(instagramAccount);
  }, [instagramAccount]);

  const generalOverviewMessages = useMemo(() => {
    if (!instagramAccount) {
      return [];
    }
    return getGeneralOverviewMessages(instagramAccount);
  }, [instagramAccount]);

  const strengthsMessages = useMemo(() => {
    if (!instagramAccount) {
      return [];
    }
    return getStrengthMessages(instagramAccount);
  }, [instagramAccount]);

  const weaknessesMessages = useMemo(() => {
    if (!instagramAccount) {
      return [];
    }
    return getWeaknessMessages(instagramAccount);
  }, [instagramAccount]);

  const monetizationMessages = useMemo(() => {
    if (!instagramAccount) {
      return [];
    }
    return getMonetizationMessages(instagramAccount);
  }, [instagramAccount]);

  return (
    <>
      <Seo title={t(tokens.landing.title) as string} />
      <Box>
        <Box
          sx={{
            pt: {
              xs: "30px",
              md: "30px",
            },
            pb: {
              xs: "50px",
              md: "50px",
            },
          }}
        >
          <InstagramSearchForm
            value={username ?? ""}
            hasGeneralError={hasGeneralError}
            hasUserNotFoundError={hasUserNotFoundError}
            isLoading={isLoading}
            onSubmit={onSubmit}
            placeholderText={t(tokens.instagram.placeholders.username)}
            buttonText={t(tokens.general.buttons.analyze)}
          />
        </Box>
        {(instagramAccount || isLoading) && (
          <>
            <Stack spacing={3}>
              <Grid
                container
                spacing={3}
                alignItems="stretch"
              >
                <Grid
                  xs={12}
                  sm={12}
                  md={8}
                >
                  <InstagramProfileCard
                    account={instagramAccount ?? undefined}
                  />
                </Grid>
                <Grid
                  xs={12}
                  sm={12}
                  md={4}
                >
                  <InstagramProfileEngagementRateCard
                    account={instagramAccount ?? undefined}
                  />
                </Grid>
              </Grid>
              {instagramAccount && (
                <CardReviewTemplate
                  messages={generalOverviewMessages}
                  title={`💎 ${t(tokens.instagram.sections.overview)}`}
                  content={instagramAccount.generatedData.overview}
                  onGenerationFinished={(text) => {
                    var newInstagramAccount = { ...instagramAccount };
                    newInstagramAccount.generatedData.overview = text;
                    setInstagramAccount(newInstagramAccount);
                    updateInstagramProfile(newInstagramAccount.generatedData);
                  }}
                />
              )}
              {instagramAccount &&
                instagramAccount.generatedData.overview &&
                !instagramAccount.data.private && (
                  <CardReviewTemplate
                    messages={engagementRateMessages}
                    title={`💬 ${t(tokens.instagram.sections.engagement)}`}
                    content={instagramAccount.generatedData.engagement}
                    onGenerationFinished={(text) => {
                      var newInstagramAccount = { ...instagramAccount };
                      newInstagramAccount.generatedData.engagement = text;
                      setInstagramAccount(newInstagramAccount);
                      updateInstagramProfile(newInstagramAccount.generatedData);
                    }}
                  />
                )}
              {instagramAccount &&
                !instagramAccount.data.private &&
                instagramAccount.generatedData.overview &&
                instagramAccount.generatedData.engagement && (
                  <CardReviewTemplate
                    messages={generalContentReviewMessages}
                    title={`🔍 ${t(tokens.instagram.sections.content)}`}
                    content={instagramAccount.generatedData.content}
                    onGenerationFinished={(text) => {
                      var newInstagramAccount = {
                        ...instagramAccount,
                      };
                      instagramAccount.generatedData.content = text;
                      setInstagramAccount(newInstagramAccount);
                      updateInstagramProfile(newInstagramAccount.generatedData);
                    }}
                  />
                )}
              {instagramAccount &&
                !instagramAccount.data.private &&
                instagramAccount.generatedData.overview &&
                instagramAccount.generatedData.engagement &&
                instagramAccount.generatedData.content && (
                  <CardReviewTemplate
                    messages={strengthsMessages}
                    title={`💪 ${t(tokens.instagram.sections.strengths)}`}
                    content={instagramAccount.generatedData.strengths}
                    onGenerationFinished={(text) => {
                      var newInstagramAccount = {
                        ...instagramAccount,
                      };
                      newInstagramAccount.generatedData.strengths = text;
                      setInstagramAccount(newInstagramAccount);
                      updateInstagramProfile(newInstagramAccount.generatedData);
                    }}
                  />
                )}
              {instagramAccount &&
                !instagramAccount.data.private &&
                instagramAccount.generatedData.overview &&
                instagramAccount.generatedData.engagement &&
                instagramAccount.generatedData.content &&
                instagramAccount.generatedData.strengths && (
                  <CardReviewTemplate
                    messages={weaknessesMessages}
                    title={`😳 ${t(tokens.instagram.sections.weaknesses)}`}
                    content={instagramAccount.generatedData.weaknesses}
                    onGenerationFinished={(text) => {
                      var newInstagramAccount = {
                        ...instagramAccount,
                      };
                      newInstagramAccount.generatedData.weaknesses = text;
                      setInstagramAccount(newInstagramAccount);
                      updateInstagramProfile(newInstagramAccount.generatedData);
                    }}
                  />
                )}
              {instagramAccount &&
                !instagramAccount.data.private &&
                instagramAccount.generatedData.overview &&
                instagramAccount.generatedData.engagement &&
                instagramAccount.generatedData.content &&
                instagramAccount.generatedData.strengths &&
                instagramAccount.generatedData.weaknesses && (
                  <CardReviewTemplate
                    messages={monetizationMessages}
                    title={`💰 ${t(tokens.instagram.sections.monetization)}`}
                    content={instagramAccount.generatedData.monetization}
                    onGenerationFinished={(text) => {
                      var newInstagramAccount = {
                        ...instagramAccount,
                      };
                      newInstagramAccount.generatedData.monetization = text;
                      setInstagramAccount(newInstagramAccount);
                      //updateInstagramProfile(newInstagramAccount.generatedData);
                    }}
                  />
                )}
              {instagramAccount &&
                instagramAccount.data.relatedProfiles.length > 0 &&
                !instagramAccount.data.private &&
                instagramAccount.generatedData.overview &&
                instagramAccount.generatedData.engagement &&
                instagramAccount.generatedData.content &&
                instagramAccount.generatedData.strengths &&
                instagramAccount.generatedData.weaknesses && (
                  <>
                    <Typography
                      variant="h4"
                      sx={{ mt: 3 }}
                    >
                      👥 {t(tokens.instagram.sections.similarProfiles)}
                    </Typography>
                    <SimilarAccounts
                      profiles={instagramAccount.data.relatedProfiles}
                    />
                  </>
                )}
              {instagramAccount && (
                <>
                  <CreateContentStrategy
                    instagramAccountId={instagramAccount.id}
                  />
                </>
              )}
            </Stack>
          </>
        )}
      </Box>
    </>
  );
};

export default Page;
